import React, { useEffect, useRef, useState } from 'react';
import { Route, Project } from '../../types';
import { useModalClose } from '../../hooks/useModalClose';

interface EdgeContextMenuProps {
  route: Route;
  project: Project;
  position: { x: number; y: number };
  onClose: () => void;
  onUpdate: (updates: Partial<Route>) => void;
  onDelete: () => void;
}

export const EdgeContextMenu: React.FC<EdgeContextMenuProps> = ({
  route,
  project,
  position,
  onClose,
  onUpdate,
  onDelete
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [distance, setDistance] = useState(route.distance);
  const [distanceCalculation, setDistanceCalculation] = useState(route.distanceCalculation);
  const [landType, setLandType] = useState(route.landType);

  // Get connected stations
  const sourceStation = project.network.stations.find(s => s.id === route.source);
  const targetStation = project.network.stations.find(s => s.id === route.target);

  // Focus input on mount if in manual mode
  React.useEffect(() => {
    if (inputRef.current && distanceCalculation === 'manual') {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [route.id, distanceCalculation]);

  useEffect(() => {
    setLandType(route.landType);
  }, [route.landType]);

  // Check which stations are missing coordinates
  const missingCoordinates = [];
  if (sourceStation && (!sourceStation.latitude || !sourceStation.longitude)) {
    missingCoordinates.push(sourceStation.name);
  }
  if (targetStation && (!targetStation.latitude || !targetStation.longitude)) {
    missingCoordinates.push(targetStation.name);
  }

  const handleSaveAndClose = async () => {
    const updates: Partial<Route> = {};
    
    // Check if distance has changed
    if (distance !== route.distance) {
      updates.distance = distance;
    }
    
    // Check if distance calculation has changed
    if (distanceCalculation !== route.distanceCalculation) {
      updates.distanceCalculation = distanceCalculation;
    }
    
    // Check if land type has changed
    if (landType !== route.landType) {
      updates.landType = landType;
    }

    // If we have any updates, save them
    if (Object.keys(updates).length > 0) {
      console.log('EdgeContextMenu:', {
        event: 'saving',
        routeId: route.id,
        updates
      });
      await onUpdate(updates);
    }
    
    onClose();
  };

  const modalRef = useModalClose({
    onClose: handleSaveAndClose,
    project,
    view: 'edge'
  });

  const handleDistanceCalculationChange = (mode: 'manual' | 'automatic') => {
    console.log('EdgeContextMenu:', {
      event: 'mode_changed',
      routeId: route.id,
      oldMode: distanceCalculation,
      newMode: mode
    });
    setDistanceCalculation(mode);
    onUpdate({ distanceCalculation: mode });
  };

  return (
    <div 
      ref={modalRef}
      className="w-[360px]"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        background: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '12px',
        zIndex: 1000
      }}
    >
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        display: 'flex',
        gap: '8px'
      }}>
        <button
          onClick={onDelete}
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            color: '#666',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#dc2626'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
          </svg>
        </button>
        <button
          onClick={handleSaveAndClose}
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            color: '#666'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div style={{ marginBottom: '8px', marginTop: '16px' }}>Distance</div>
      <div className="flex gap-2">
        <button
          onClick={() => handleDistanceCalculationChange('manual')}
          className={`px-3 py-1.5 rounded-md text-sm ${
            distanceCalculation === 'manual'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Manual ({Math.ceil(route.distance).toLocaleString()} km)
        </button>
        <button
          onClick={() => handleDistanceCalculationChange('automatic')}
          className={`px-3 py-1.5 rounded-md text-sm ${
            distanceCalculation === 'automatic'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Automatic {route.automaticDistance ? `(${Math.ceil(route.automaticDistance).toLocaleString()} km)` : '(N/A)'}
        </button>
      </div>
      
      {distanceCalculation === 'manual' && (
        <div className="mt-2">
          <input
            ref={inputRef}
            type="number"
            value={distance}
            onChange={(e) => setDistance(Number(e.target.value))}
            onBlur={() => {
              if (distance !== route.distance) {
                onUpdate({ distance });
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSaveAndClose();
              }
            }}
            className="w-full px-3 py-1.5 border rounded-md"
            placeholder="Distance in km"
          />
          <div className="mt-2 text-xs text-gray-500 break-normal">
            Minimum configured route calculation distance is {project.config.calculation.minimumRouteDistance} km
          </div>
        </div>
      )}
      
      {distanceCalculation === 'automatic' && (
        <div className="mt-2">
          {missingCoordinates.length > 0 ? (
            <div className="text-sm text-amber-600">
              Add coordinates for {missingCoordinates.length === 1 ? missingCoordinates[0] : `${missingCoordinates.join(' and ')}`}
            </div>
          ) : route.automaticDistance ? (
            <div className="text-xs text-gray-500 break-normal">
              Distance calculated from GPS coordinates, with a minimum configured route calculation distance of {project.config.calculation.minimumRouteDistance} km
            </div>
          ) : null}
        </div>
      )}
      
      <div style={{ marginBottom: '8px', marginTop: '16px' }}>Border</div>
      <select
        value={landType}
        onChange={(e) => {
          const newLandType = e.target.value as 'domestic' | 'customs' | 'no-customs';
          setLandType(newLandType);
          onUpdate({ landType: newLandType });
        }}
        className="w-full px-3 py-1.5 border rounded-md text-sm bg-white"
      >
        <option value="domestic">No crossing</option>
        <option value="no-customs">Border without customs</option>
        <option value="customs">Border with customs</option>
      </select>
    </div>
  );
}; 